import { QueryClient } from "@tanstack/react-query";

import {
  formatErrorForLog,
  isAuthError,
  isNetworkError,
  isOAuthTokenUnavailable,
  trackNetworkError,
} from "./errorUtils";
import { isClientSignOutInProgress } from "./signOutGate";

/**
 * Minimal logger interface that both the web `StaticLogger` (`message, ...args`)
 * and the desktop pino child logger (`message, ...args`) satisfy.
 */
export interface QueryLogger {
  error: (message: string, ...args: unknown[]) => void;
  debug: (message: string, ...args: unknown[]) => void;
  /** Optional; when missing, auth issues fall back to `debug`. */
  warn?: (message: string, ...args: unknown[]) => void;
}

const NO_OP_LOGGER: QueryLogger = {
  error: () => {},
  debug: () => {},
};

/**
 * Number of times a transient auth (401) failure is retried before the
 * error is surfaced.
 *
 * A 401 frequently means the session token was mid-refresh when the request
 * was sent (clock skew, token rotation, waking from sleep). Retrying with the
 * shared exponential backoff gives the auth layer time to re-establish a valid
 * session before the next attempt, which recovers automatically in the common
 * case instead of failing the query/mutation outright.
 */
export const AUTH_RETRY_LIMIT = 2;

/** Number of times network errors are retried (e.g. flaky connection, sleep/wake). */
const NETWORK_RETRY_LIMIT = 3;

export interface CreateQueryClientOptions {
  /** Logger used for global query/mutation error handlers. Falls back to a no-op logger. */
  logger?: QueryLogger;
}

/**
 * Creates a `QueryClient` with shared defaults used across web and desktop.
 *
 * Includes:
 * - Exponential backoff with jitter for retries
 * - Auth-aware and network-aware retry logic
 * - Global error handlers with sleep/wake detection
 * - Aggressive GC to prevent memory bloat during long sessions
 */
export function createQueryClient(
  options?: CreateQueryClientOptions
): QueryClient {
  const logger = options?.logger ?? NO_OP_LOGGER;

  const retryDelay = (attemptIndex: number): number => {
    const base = Math.min(1000 * 2 ** attemptIndex, 30_000);
    const jitter = base * (0.3 + Math.random() * 0.4);
    return Math.round(jitter);
  };

  const retry = (failureCount: number, error: Error): boolean => {
    if (isClientSignOutInProgress()) return false;
    if (isAuthError(error)) {
      // OAuth provider token missing (e.g. Spotify/Google disconnected) is not
      // transient — the member must reconnect, so retrying never succeeds.
      if (isOAuthTokenUnavailable(error)) return false;
      // Otherwise treat it as a transient session error: retry (with backoff)
      // to let auth re-establish before giving up.
      return failureCount < AUTH_RETRY_LIMIT;
    }
    if (isNetworkError(error)) return failureCount < NETWORK_RETRY_LIMIT;
    const status = (error as unknown as { status?: number })?.status;
    return failureCount < NETWORK_RETRY_LIMIT && status !== 404;
  };

  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // Wake/resume is coordinated via explicit invalidation + wakeRecoveryGate;
        // global focus/reconnect refetch caused overlapping storms with pollers.
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        retryDelay,
        retry,
        staleTime: 1000 * 60 * 2,
        gcTime: 1000 * 60 * 2,
        structuralSharing: true,
        networkMode: "online",
      },
      mutations: {
        retryDelay,
        retry,
        gcTime: 1000 * 30,
        networkMode: "online",
      },
    },
  });

  client.getQueryCache().config.onError = (error) => {
    if (isAuthError(error)) {
      if (isOAuthTokenUnavailable(error)) {
        logger.debug(
          `[queryClient] OAuth token not available (reconnect needed): ${formatErrorForLog(error)}`
        );
      } else {
        const msg = `[queryClient] Query auth error: ${formatErrorForLog(error)}`;
        if (typeof logger.warn === "function") {
          logger.warn(msg, error);
        } else {
          logger.debug(msg, error);
        }
      }
    } else if (isNetworkError(error)) {
      const isWakingFromSleep = trackNetworkError();
      if (isWakingFromSleep) {
        logger.debug(
          "[queryClient] Network error (likely waking from sleep):",
          error
        );
      } else {
        logger.debug("[queryClient] Query network error:", error);
      }
    }
  };

  client.getMutationCache().config.onError = (error) => {
    if (isAuthError(error)) {
      if (isOAuthTokenUnavailable(error)) {
        logger.debug(
          `[queryClient] OAuth token not available (reconnect needed): ${formatErrorForLog(error)}`
        );
      } else {
        const msg = `[queryClient] Mutation auth error: ${formatErrorForLog(error)}`;
        if (typeof logger.warn === "function") {
          logger.warn(msg, error);
        } else {
          logger.debug(msg, error);
        }
      }
    } else if (isNetworkError(error)) {
      const isWakingFromSleep = trackNetworkError();
      if (isWakingFromSleep) {
        logger.debug(
          "[queryClient] Network error (likely waking from sleep):",
          error
        );
      } else {
        logger.debug("[queryClient] Mutation network error:", error);
      }
    }
  };

  return client;
}
